<template>
	<div class="task-list-cell-start-date">
		<AppDatePickerInline
			:open
			:model-value="committedStartDate"
			:format="dateFormat"
			check-weekends
			append-to-body
			:date-disabled="isAfterDueDate"
			@update:model-value="onUpdateDate"
			@close="open = false"
		>
			<button
				type="button"
				class="task-list-cell-start-date__trigger"
				:aria-label="$t('start_date')"
				@click="open = !open"
			>
				<span class="task-list-cell-start-date__label">
					{{ displayLabel }}
				</span>
			</button>
		</AppDatePickerInline>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { AppDatePickerInline } from '@/features/App';
import {
	useTaskListTaskSaveMutation,
	type Task,
	type TaskListTaskFieldsFragment,
	type TaskListTaskFragment,
} from '@/generated/graphql';
import { useFormatter, useOptimisticSave } from '@/composables';
import { resetSapiDateString, resetTime } from '@/utils/formatting';

export type TaskListCellStartDateProps = {
	taskId: Task['id'];
	startDate: TaskListTaskFieldsFragment['startDate'];
	useDeadlineWithTime: boolean;
	dueDate: TaskListTaskFieldsFragment['dueDate'];
	dueDateTime: TaskListTaskFieldsFragment['dueDateTime'];
};

const props = defineProps<TaskListCellStartDateProps>();

const emit = defineEmits<{
	'task-updated': [task?: TaskListTaskFragment];
}>();

const { dateFormatForSUIdatePicker: dateFormat, formatDateNoCurrentYear } =
	useFormatter();
const { mutate } = useTaskListTaskSaveMutation();

const { committedValue: committedStartDate, save } = useOptimisticSave(
	() => props.startDate || ''
);
const open = ref(false);

const effectiveDue = computed(() => {
	if (props.useDeadlineWithTime) {
		return props.dueDateTime || '';
	}

	return props.dueDate || '';
});

const displayLabel = computed(() => {
	const raw = committedStartDate.value;
	if (!raw) {
		return '';
	}

	return formatDateNoCurrentYear(resetSapiDateString(raw));
});

const isAfterDueDate = (date: string) => {
	if (!effectiveDue.value) {
		return false;
	}

	return resetTime(date) > resetTime(resetSapiDateString(effectiveDue.value));
};

const onUpdateDate = async (value: string) => {
	open.value = false;
	if (!value) {
		return;
	}

	await save(value, async () => {
		const result = await mutate({
			input: {
				taskId: props.taskId,
				startDate: value,
			},
		});

		if (result?.data?.TaskSave) {
			emit('task-updated', result.data.TaskSave);
			return result.data.TaskSave.startDate || undefined;
		}
	});
};
</script>

<style scoped lang="scss">
.task-list-cell-start-date {
	width: 100%;

	&__trigger {
		display: inline-flex;
		justify-content: flex-end;
		align-items: center;
		width: calc(100% + 1rem);
		height: 4rem;
		border-radius: 0.5rem;
		text-align: right;
		margin: -1rem;
		padding: 1rem;

		&:hover {
			background-color: $neutral-20;
		}
	}

	&__label {
		font-feature-settings: 'tnum';
	}

	:deep(.mx-datepicker) {
		width: 100%;
	}
}
</style>
